/* Wrapped in an IIFE so its top-level names stay out of the global scope.
   Theme scripts share one global namespace: when two of them declared the same
   const, the second failed to parse outright and silently did nothing. */
(() => {
  /**
   * Variant selection and the product media gallery.
   *
   * Variant resolution happens on the client from a list the section already
   * rendered, so switching options is instant; only the URL is updated afterwards so
   * the selection survives a reload or a shared link.
   */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form') || document.querySelector('[data-product-form]');
      this.variants = JSON.parse(this.querySelector('[data-variant-data]')?.textContent || '[]');
      this.addEventListener('change', this.#onChange);
    }

    #onChange = () => {
      const selected = [...this.querySelectorAll('.variant-option__input:checked')].map(
        (input) => input.value
      );

      const variant = this.variants.find(
        (candidate) =>
          candidate.options.length === selected.length &&
          candidate.options.every((value, index) => value === selected[index])
      );

      this.#reflectLabels();
      if (!variant) return;

      this.#setFormVariant(variant);
      this.#updatePrice(variant);
      this.#showMedia(variant);
      this.#updateUrl(variant);

      document.dispatchEvent(new CustomEvent('variant:change', { detail: variant }));
    };

    /** The legend echoes the current choice, the way the reference layout does. */
    #reflectLabels() {
      this.querySelectorAll('.variant-option').forEach((option) => {
        const checked = option.querySelector('.variant-option__input:checked');
        const output = option.querySelector('[data-option-value]');
        if (checked && output) output.textContent = checked.value;
      });
    }

    #setFormVariant(variant) {
      const input = this.form?.querySelector('[data-variant-id]');
      if (input) input.value = variant.id;

      const button = this.form?.querySelector('[data-add-button]');
      if (!button) return;
      button.disabled = !variant.available;
      button.textContent = button.dataset[variant.available ? 'labelAvailable' : 'labelSoldOut'];
    }

    #updatePrice(variant) {
      const host = document.querySelector('[data-product-price]');
      if (!host) return;
      host.innerHTML = this.#renderPrice(variant, host.dataset);
    }

    #renderPrice(variant, options = {}) {
      const money = (cents) =>
        new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: options.currencyCode || window.Shopify?.currency?.active || 'USD',
        }).formatToParts(cents / 100)
          .map((part) => part.type === 'currency' && options.currencySymbol ? options.currencySymbol : part.value)
          .join('');

      // Order matches the Liquid: what is being charged, then what it was.
      const onSale = variant.compare_at_price > variant.price;

      // The saving is only offered when the Liquid left a label to fill in, which
      // is how the block's "show savings" setting reaches the script.
      let save = '';
      if (onSale && options.saveLabel) {
        const saved = variant.compare_at_price - variant.price;
        const value =
          options.saveFormat === 'percent'
            ? `${Math.round((saved / variant.compare_at_price) * 100)}%`
            : money(saved);
        save = `<span class="price__save">${options.saveLabel.replace('[[value]]', value)}</span>`;
      }

      return `
        <span class="price__current">${money(variant.price)}</span>
        ${onSale ? `<s class="price__was">${money(variant.compare_at_price)}</s>` : ''}
        ${save}
      `;
    }

    /** Scroll the gallery to the media this variant is pinned to, if any. */
    #showMedia(variant) {
      if (!variant.featured_media_id) return;
      const slide = document.getElementById(`GalleryItem-${variant.featured_media_id}`);
      const track = slide?.parentElement;
      if (!slide || !track) return;
      const gallery = track.closest('product-gallery');
      if (gallery?.showMedia) {
        gallery.showMedia(slide);
        return;
      }
      // Setting scrollLeft directly, not scrollIntoView: the latter walks up and
      // scrolls every scrollable ancestor, which drags the page sideways too.
      track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }

    /** replaceState, not push: option changes are refinements, not history steps. */
    #updateUrl(variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', VariantPicker);
  }

  /**
   * <product-gallery> — arrows and a counter over a scroll-snap track.
   *
   * The browser owns the scrolling; this only nudges it and reports position, so
   * touch gestures and momentum keep working untouched.
   */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      // There can be two: the visible pager and the hidden aria-live readout.
      this.counters = [...this.querySelectorAll('[data-gallery-current]')];
      this.counter = this.counters[0];
      if (!this.viewport) return;
      this.galleryEvents?.abort();
      this.galleryEvents = new AbortController();
      const options = { signal: this.galleryEvents.signal };
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.activeIndex = -1;
      this.#setActive(0);

      // The zoom viewer works for any number of images, one included, so it is wired
      // up first — an early return for the single-slide case used to sit in front of
      // it, which is what left one-image products unable to open the lightbox at all.
      this.#setupLightbox();

      // Arrows, thumbnails and the position observer only have work to do with 2+.
      if (this.slides.length < 2) return;

      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.#step(-1), options);
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#step(1), options);

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.#goTo(Number(thumb.dataset.galleryThumb)), options);
      });

      // A requested slide stays selected during travel; swipes sync once settled.
      this.viewport.addEventListener('scroll', () => {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.#settle(), 150);
      }, { ...options, passive: true });
      this.viewport.addEventListener('scrollend', () => this.#settle(), options);
      this.galleryWidth = this.viewport.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.viewport.clientWidth;
        if (!width || width === this.galleryWidth) return;
        this.galleryWidth = width;
        this.#goTo(this.activeIndex, 'instant');
      });
      this.resizeObserver.observe(this.viewport);
    }

    /* Wires the full-size viewer end to end: the zoom controls, wheel and gesture
       zooming, one-finger pan and two-finger pinch, thumbnail switching, and the ways
       it is dismissed. Kept out of connectedCallback so it can run for a lone image. */
    #setupLightbox() {
      this.querySelectorAll('[data-gallery-zoom]').forEach((trigger) => {
        trigger.addEventListener('click', () =>
          this.#openLightbox(Number(trigger.dataset.galleryZoom))
        );
      });

      // The expand (plus) opens the still viewer at whichever frame is showing.
      this.querySelectorAll('[data-gallery-expand]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const current = Number(this.counter?.textContent || 1) - 1;
          this.#openLightbox(current);
        });
      });

      this.#setupVideoModal();

      // The 360 button opens the spin viewer if a video is present, otherwise it falls
      // back to the still viewer so the button is never dead.
      this.#setupViewer360();
      if (!this.#viewer360) {
        this.querySelectorAll('[data-gallery-360]').forEach((trigger) => {
          trigger.addEventListener('click', () => {
            const current = Number(this.counter?.textContent || 1) - 1;
            this.#openLightbox(current);
          });
        });
      }

      const lightbox = this.#lightbox;
      if (!lightbox) return;

      // Magnification and the pan offset that goes with it. The image is transformed,
      // not scrolled — a CSS transform grows no scrollable area, so panning has to be
      // driven by hand rather than by the stage's scrollbars.
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;

      lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () =>
        lightbox.close()
      );

      lightbox.querySelector('[data-lightbox-zoom-in]')?.addEventListener('click', () =>
        this.#setScale(this.scale + 0.5)
      );
      lightbox.querySelector('[data-lightbox-zoom-out]')?.addEventListener('click', () =>
        this.#setScale(this.scale - 0.5)
      );

      const stage = lightbox.querySelector('[data-lightbox-stage]');
      const image = lightbox.querySelector('[data-lightbox-image]');

      // With a single frame there is nothing to scroll between, so the wheel zooms
      // outright — toward the pointer, so the detail under the cursor stays put.
      stage?.addEventListener(
        'wheel',
        (event) => {
          event.preventDefault();
          this.#setScale(this.scale + (event.deltaY < 0 ? 0.2 : -0.2), event);
        },
        { passive: false }
      );

      // Touch and mouse gestures, unified through pointer events. One finger pans a
      // magnified image; two fingers pinch to zoom and pan at once, anchored to the
      // fingers so the picture follows them rather than jumping. Tracking every active
      // pointer is what stops the earlier flicker: a second finger no longer hijacks
      // the one-finger drag and fight it frame by frame.
      const pointers = new Map();
      let moved = false;
      let panStartX = 0;
      let panStartY = 0;
      let panStartTx = 0;
      let panStartTy = 0;
      let pinch = null;

      const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
      const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

      const beginPan = (point) => {
        panStartX = point.x;
        panStartY = point.y;
        panStartTx = this.tx;
        panStartTy = this.ty;
      };

      const beginPinch = (pts) => {
        const rect = image.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const m = midpoint(pts[0], pts[1]);
        pinch = {
          d0: distance(pts[0], pts[1]) || 1,
          s0: this.scale,
          // The layout centre is fixed while the box is; the midpoint's offset from
          // the current visual centre is the anchor the zoom keeps under the fingers.
          cLayoutX: cx - this.tx,
          cLayoutY: cy - this.ty,
          anchorX: m.x - cx,
          anchorY: m.y - cy,
        };
        moved = true;
      };

      image?.addEventListener('pointerdown', (event) => {
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        try {
          image.setPointerCapture(event.pointerId);
        } catch (e) {
          /* capture unsupported */
        }
        const pts = [...pointers.values()];
        if (pts.length === 1) {
          moved = false;
          beginPan(pts[0]);
        } else if (pts.length === 2) {
          beginPinch(pts);
        }
        event.preventDefault();
      });

      image?.addEventListener('pointermove', (event) => {
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const pts = [...pointers.values()];

        if (pts.length >= 2 && pinch) {
          const d = distance(pts[0], pts[1]) || 1;
          const m = midpoint(pts[0], pts[1]);
          const next = Math.min(4, Math.max(1, pinch.s0 * (d / pinch.d0)));
          const k = next / pinch.s0;
          this.scale = next;
          // Put the anchored content point back under the (possibly moved) midpoint.
          this.tx = m.x - pinch.cLayoutX - k * pinch.anchorX;
          this.ty = m.y - pinch.cLayoutY - k * pinch.anchorY;
          this.#clampPan();
          this.#applyTransform();
        } else if (pts.length === 1 && this.scale > 1) {
          const dx = pts[0].x - panStartX;
          const dy = pts[0].y - panStartY;
          if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
          this.tx = panStartTx + dx;
          this.ty = panStartTy + dy;
          this.#clampPan();
          this.#applyTransform();
        }
      });

      const endPointer = (event) => {
        if (!pointers.has(event.pointerId)) return;
        pointers.delete(event.pointerId);
        try {
          image.releasePointerCapture(event.pointerId);
        } catch (e) {
          /* already released */
        }
        const pts = [...pointers.values()];
        if (pts.length < 2) pinch = null;
        // Lifting one finger of a pinch: re-anchor the pan to the survivor so it does
        // not lurch to a stale baseline.
        if (pts.length === 1) beginPan(pts[0]);
      };
      image?.addEventListener('pointerup', endPointer);
      image?.addEventListener('pointercancel', endPointer);

      // Click to zoom in a step, or back to fit once magnified — but a drag or pinch
      // that ends in a click must not also toggle the zoom.
      image?.addEventListener('click', (event) => {
        if (moved) {
          moved = false;
          return;
        }
        this.#setScale(this.scale > 1 ? 1 : 2, event);
      });

      this.lightboxData = JSON.parse(
        lightbox.querySelector('[data-lightbox-data]')?.textContent || '[]'
      );

      // Full-size sources are heavy, so swapping them cold reads as a multi-second
      // stall. Hold each one as an Image once fetched — the browser keeps the decoded
      // bitmap, so a revisit is instant — and warm the neighbours ahead of the tap
      // that asks for them.
      this.lightboxCache = new Map();

      lightbox.querySelectorAll('[data-lightbox-thumb]').forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.#showLightboxImage(index));
      });

      lightbox.addEventListener('close', () => this.#setScale(1));
      // A modal dialog fills the viewport, so a backdrop click still lands on the
      // dialog; compare against its box to tell the two apart.
      lightbox.addEventListener('click', (event) => {
        if (event.target !== lightbox) return;
        const box = lightbox.getBoundingClientRect();
        const inside =
          event.clientX >= box.left && event.clientX <= box.right &&
          event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside) lightbox.close();
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.galleryEvents?.abort();
      clearTimeout(this.settleTimer);
      clearTimeout(this.snapTimer);
      if (this.viewport) this.viewport.style.scrollSnapType = '';
    }

    /* Opens the full-size viewer at the frame that was clicked. The dialog is shown
       first so the stage has real dimensions before the image loads into it. */
    #openLightbox(index) {
      const dialog = this.#lightbox;
      if (!dialog) return;

      dialog.showModal();
      this.#setScale(1);
      this.#showLightboxImage(index);
    }

    /* Fetches a full-size source into the cache without displaying it, so the next
       image is already in memory before it is asked for. */
    #preloadLightbox(index) {
      if (index < 0 || index >= (this.lightboxData?.length || 0)) return;
      if (this.lightboxCache?.has(index)) return;
      const item = this.lightboxData[index];
      if (!item) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = item.src;
      this.lightboxCache.set(index, img);
    }

    /* Swaps the stage image. No transition: a fade between two photographs of the
       same product reads as a glitch rather than as motion. */
    #showLightboxImage(index) {
      const dialog = this.#lightbox;
      const image = dialog?.querySelector('[data-lightbox-image]');
      const item = this.lightboxData?.[index];
      if (!image || !item) return;

      const stage = dialog.querySelector('[data-lightbox-stage]');

      // Drive the stage from an explicit full-size source, never the responsive
      // srcset the markup ships with. That srcset is resolved once, while the dialog
      // is still display:none — a zero-width slot — so left in place the opening
      // image loads and stays at a tiny candidate; dropping it forces the real size.
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.alt = item.alt || '';
      image.src = item.src;

      // Warm this frame and its neighbours so a revisit or the next tap in either
      // direction is served from cache and swaps instantly.
      this.#preloadLightbox(index);
      this.#preloadLightbox(index - 1);
      this.#preloadLightbox(index + 1);

      // Spinner over the outgoing frame, but only while the new source is genuinely
      // still on its way — a cached one is already complete, so it never flashes.
      if (image.complete && image.naturalWidth > 0) {
        stage?.removeAttribute('data-loading');
      } else {
        stage?.setAttribute('data-loading', '');
        const done = () => stage?.removeAttribute('data-loading');
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
      }

      this.#setScale(1);
      this.#markLightboxThumb(index);
    }

    get #lightbox() {
      return (
        this.parentElement?.querySelector('[data-gallery-lightbox]')
        || document.querySelector('[data-gallery-lightbox]')
      );
    }

    /* The play-video modal. Nothing to drive but opening and closing: the player
       inside is the browser's own, which handles scrubbing, volume and fullscreen
       better than anything built here. Playback is stopped on close so a clip
       can't keep running behind a closed dialog. */
    #setupVideoModal() {
      const dialog =
        this.parentElement?.querySelector('[data-video-modal]')
        || document.querySelector('[data-video-modal]');
      const triggers = this.querySelectorAll('[data-gallery-video]');
      if (!dialog || !triggers.length) return;

      const video = dialog.querySelector('video');

      const close = () => {
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
        dialog.close();
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          dialog.showModal();
          video?.play().catch(() => {});
        });
      });

      dialog.querySelector('[data-video-modal-close]')?.addEventListener('click', close);
      // Escape closes the dialog on its own; this is what stops the sound.
      dialog.addEventListener('close', () => {
        if (!video) return;
        video.pause();
        video.currentTime = 0;
      });
      // A click on the backdrop rather than the player.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      });
    }

    get #viewer360() {
      return (
        this.parentElement?.querySelector('[data-viewer-360]')
        || document.querySelector('[data-viewer-360]')
      );
    }

    /* The spin viewer. The ruler turns the model (seeking the clip — one full ruler is
       one full turn), a play/pause button spins it on its own, and the stage itself is
       for looking closer: pinch or wheel to zoom, drag to pan, exactly like the still
       lightbox. Seeks are paced to real presented frames (requestVideoFrameCallback
       where available), one in flight at a time chasing the newest target, so turning
       stays as smooth as the clip can decode. */
    #setupViewer360() {
      const dialog = this.#viewer360;
      if (!dialog) return;

      const video = dialog.querySelector('[data-viewer-360-video]');
      const stage = dialog.querySelector('[data-viewer-360-stage]');
      const scrub = dialog.querySelector('[data-viewer-360-scrub]');
      const scaleReadout = dialog.querySelector('[data-viewer-360-scale]');
      const playButton = dialog.querySelector('[data-viewer-360-play]');
      const hint = dialog.querySelector('[data-viewer-360-hint]');
      if (!video || !stage) return;

      const scrubMax = Number(scrub?.max || 1000);
      const hasFrameCallback = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;
      let targetFraction = 0;

      const setScrub = (fraction) => {
        if (scrub) scrub.value = String(Math.round(fraction * scrubMax));
      };

      // Runs cb once the next frame is actually on screen — the true cadence of the
      // decoder — falling back to the seek/paint events where rVFC is unavailable.
      const afterFrame = (cb) => {
        if (hasFrameCallback) video.requestVideoFrameCallback(() => cb());
        else video.addEventListener('seeked', () => cb(), { once: true });
      };

      const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, value));

      // ---- Zoom + pan (like the still lightbox: pinch to zoom, drag to move) ----
      const minZoom = 1;
      const maxZoom = 3;
      let zoom = 1;
      let panX = 0;
      let panY = 0;

      // The video element fills the stage, so its scaled box overspills by
      // stage * (zoom - 1); half of that each way is how far it can be dragged.
      const clampPan = () => {
        const maxX = Math.max(0, (stage.clientWidth * (zoom - 1)) / 2);
        const maxY = Math.max(0, (stage.clientHeight * (zoom - 1)) / 2);
        panX = clamp(panX, -maxX, maxX);
        panY = clamp(panY, -maxY, maxY);
      };

      const render = () => {
        clampPan();
        video.style.transform = `translate(${panX}px, ${panY}px) scale(${zoom})`;
        if (scaleReadout) scaleReadout.textContent = `${Math.round(zoom * 100)}%`;
      };

      const setZoom = (value) => {
        zoom = clamp(Math.round(value * 10) / 10, minZoom, maxZoom);
        if (zoom === 1) {
          panX = 0;
          panY = 0;
        }
        render();
      };

      // ---- Scrubbing ----
      // One seek in flight, re-issued the instant its frame is presented, always for the
      // latest target. Pacing to presented frames (not a timer, not a backlog of seeks)
      // is what keeps a drag at the clip's real frame rate.
      let seeking = false;
      const pursue = () => {
        if (seeking) return;
        const d = video.duration;
        if (!Number.isFinite(d) || d <= 0) return;
        const t = targetFraction * d;
        if (Math.abs(video.currentTime - t) < 1 / 120) return;
        seeking = true;
        video.currentTime = t;
        afterFrame(() => {
          seeking = false;
          pursue();
        });
      };

      // Seek to a fraction [0,1] of the clip.
      const seek = (fraction) => {
        targetFraction = ((fraction % 1) + 1) % 1;
        setScrub(targetFraction);
        pursue();
      };

      // ---- The prompt above the ruler ----
      const revealHint = () => {
        if (!hint) return;
        hint.hidden = false;
        hint.classList.remove('is-breathing');
        void hint.offsetWidth; // restart the breathe
        hint.classList.add('is-breathing');
      };
      const hideHint = () => {
        if (!hint) return;
        hint.hidden = true;
        hint.classList.remove('is-breathing');
      };

      // ---- Play / pause ----
      let autoStop = null; // fraction to pause at during the intro spin, or null
      const syncPlayingUI = () => playButton?.classList.toggle('is-playing', !video.paused);

      // End the intro spin: pause, then breathe the prompt to invite a drag.
      const stopIntro = () => {
        autoStop = null;
        video.pause();
        syncPlayingUI();
        revealHint();
      };

      // While playing, drag the ruler along with the clip and honour the intro stop.
      const followPlayback = () => {
        if (video.paused) return;
        const d = video.duration;
        if (Number.isFinite(d) && d > 0) {
          targetFraction = video.currentTime / d;
          setScrub(targetFraction);
          if (autoStop != null && video.currentTime >= autoStop * d) {
            stopIntro();
            return;
          }
        }
        if (hasFrameCallback) video.requestVideoFrameCallback(followPlayback);
        else requestAnimationFrame(followPlayback);
      };

      // A visibility-independent backup for the intro stop: timeupdate keeps firing even
      // when rVFC / rAF (which need a visible tab) are paused.
      video.addEventListener('timeupdate', () => {
        const d = video.duration;
        if (autoStop != null && Number.isFinite(d) && d > 0 && video.currentTime >= autoStop * d) stopIntro();
      });

      const play = () => {
        video
          .play()
          .then(() => {
            syncPlayingUI();
            if (hasFrameCallback) video.requestVideoFrameCallback(followPlayback);
            else requestAnimationFrame(followPlayback);
          })
          .catch(() => {});
      };
      const pause = () => {
        video.pause();
        syncPlayingUI();
      };

      playButton?.addEventListener('click', () => {
        autoStop = null; // the viewer takes over the intro
        hideHint();
        if (video.paused) play();
        else pause();
      });

      // Paint the first frame once the clip is ready.
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = 0;
      });

      // ---- Stage gestures: pinch to zoom, drag to pan. Never rotate — turning the
      // model is the ruler's job, so a drag on the picture only moves it. ----
      const pointers = new Map();
      let gesture = null;

      const centreOf = (points) => ({
        x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
        y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
      });

      // Snapshot the current transform and pointer positions as the reference the next
      // moves are measured against — re-taken whenever a finger lands or lifts.
      const baseline = () => {
        const points = [...pointers.values()];
        if (!points.length) {
          gesture = null;
          return;
        }
        const centre = centreOf(points);
        gesture = { zoom, panX, panY, cx: centre.x, cy: centre.y, dist: 0 };
        if (points.length >= 2) {
          gesture.dist = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) || 1;
        }
      };

      stage.addEventListener('pointerdown', (event) => {
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        try {
          stage.setPointerCapture(event.pointerId);
        } catch {
          /* a pointer that can't be captured is still tracked */
        }
        baseline();
      });

      stage.addEventListener('pointermove', (event) => {
        if (!pointers.has(event.pointerId) || !gesture) return;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const points = [...pointers.values()];
        const centre = centreOf(points);
        if (points.length >= 2) {
          const dist = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) || 1;
          zoom = clamp(gesture.zoom * (dist / gesture.dist), minZoom, maxZoom);
          panX = gesture.panX + (centre.x - gesture.cx);
          panY = gesture.panY + (centre.y - gesture.cy);
          render();
        } else if (zoom > 1) {
          panX = gesture.panX + (centre.x - gesture.cx);
          panY = gesture.panY + (centre.y - gesture.cy);
          render();
        }
      });

      const dropPointer = (event) => {
        pointers.delete(event.pointerId);
        try {
          stage.releasePointerCapture(event.pointerId);
        } catch {
          /* nothing to release */
        }
        baseline(); // re-baseline whatever fingers remain
        if (zoom === 1) {
          panX = 0;
          panY = 0;
          render();
        }
      };
      stage.addEventListener('pointerup', dropPointer);
      stage.addEventListener('pointercancel', dropPointer);

      // Wheel zooms as well.
      stage.addEventListener(
        'wheel',
        (event) => {
          event.preventDefault();
          setZoom(zoom + (event.deltaY < 0 ? 0.2 : -0.2));
        },
        { passive: false }
      );

      // ---- The ruler rotates (and clears the prompt / stops any autoplay) ----
      scrub?.addEventListener('input', () => {
        hideHint();
        pause();
        autoStop = null;
        seek(Number(scrub.value) / scrubMax);
      });

      // ---- Zoom ----
      dialog.querySelector('[data-viewer-360-zoom-in]')?.addEventListener('click', () => setZoom(zoom + 0.5));
      dialog.querySelector('[data-viewer-360-zoom-out]')?.addEventListener('click', () => setZoom(zoom - 0.5));

      // ---- Open / close ----
      // Opening resets to the first frame at 100%, then spins to ~40% and stops there.
      this.querySelectorAll('[data-gallery-360]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          dialog.showModal();
          seeking = false;
          targetFraction = 0;
          video.currentTime = 0;
          setScrub(0);
          // A phone opens zoomed in so the model fills the narrow screen; a desktop,
          // with room to spare, opens at 100%.
          setZoom(window.matchMedia('(max-width: 48rem)').matches ? 1.5 : 1);
          hideHint();
          autoStop = 0.4;
          play();
        });
      });

      const closeViewer = () => {
        pause();
        autoStop = null;
        hideHint();
        dialog.close();
      };
      dialog.querySelector('[data-viewer-360-close]')?.addEventListener('click', closeViewer);
      // Click on the backdrop (the dialog itself, outside the stage) closes it.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeViewer();
      });
    }

    /* Sets the magnification. With a pointer event it zooms toward the cursor —
       the point under it keeps its place — otherwise it grows from the centre.
       Dropping back to 1 clears the pan so the next open starts centred. */
    #setScale(value, event) {
      const dialog = this.#lightbox;
      if (!dialog) return;
      const image = dialog.querySelector('[data-lightbox-image]');

      const prev = this.scale;
      const next = Math.min(4, Math.max(1, Number(value.toFixed(2))));

      // Keep the point under the cursor fixed: its distance from the image's centre
      // grows with the zoom, so the translation takes up the difference.
      if (image && event && next > 1 && next !== prev) {
        const rect = image.getBoundingClientRect();
        const px = event.clientX - (rect.left + rect.width / 2);
        const py = event.clientY - (rect.top + rect.height / 2);
        const factor = next / prev;
        this.tx -= px * (factor - 1);
        this.ty -= py * (factor - 1);
      }

      this.scale = next;
      if (this.scale === 1) {
        this.tx = 0;
        this.ty = 0;
      }
      this.#clampPan();
      this.#applyTransform();
    }

    /* Holds the pan within bounds, so the picture can never be dragged off the stage:
       the furthest it can move is half of whatever the scaled image overspills by. */
    #clampPan() {
      const dialog = this.#lightbox;
      const image = dialog?.querySelector('[data-lightbox-image]');
      const stage = dialog?.querySelector('[data-lightbox-stage]');
      if (!image || !stage) return;
      const maxX = Math.max(0, (image.clientWidth * this.scale - stage.clientWidth) / 2);
      const maxY = Math.max(0, (image.clientHeight * this.scale - stage.clientHeight) / 2);
      this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
      this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
    }

    /* One transform carries both zoom and pan, so the browser keeps the image on the
       compositor and neither costs layout. */
    #applyTransform() {
      const dialog = this.#lightbox;
      if (!dialog) return;
      const image = dialog.querySelector('[data-lightbox-image]');
      if (image) {
        image.style.setProperty('--lb-scale', this.scale);
        image.style.setProperty('--lb-tx', `${this.tx}px`);
        image.style.setProperty('--lb-ty', `${this.ty}px`);
      }
      dialog.toggleAttribute('data-zoomed', this.scale > 1);

      const readout = dialog.querySelector('[data-lightbox-scale]');
      if (readout) readout.textContent = `${Math.round(this.scale * 100)}%`;
    }

    #markLightboxThumb(index) {
      this.#lightbox?.querySelectorAll('[data-lightbox-thumb]').forEach((thumb, i) => {
        const selected = i === index;
        thumb.setAttribute('aria-selected', String(selected));
        if (selected) this.#revealThumb(thumb);
      });
      const readout = this.#lightbox?.querySelector('[data-lightbox-current]');
      if (readout) readout.textContent = index + 1;
    }

    /* Brings the active thumbnail to the middle of the rail, so a run of its
       neighbours comes into view at once instead of edging in one at a time — the
       scroll clamps itself at either end, so the first and last thumbnails still sit
       flush. A thumbnail already comfortably on screen is left where it is, so
       browsing the middle of the strip doesn't jitter. Only the rail's own scroll
       moves, never an ancestor, so the page holds still. */
    #revealThumb(thumb) {
      const rail = thumb?.parentElement;
      if (!rail || !thumb) return;
      const railBox = rail.getBoundingClientRect();
      const thumbBox = thumb.getBoundingClientRect();

      const siblings = [...rail.children];
      const index = siblings.indexOf(thumb);
      // The rail is vertical beside the main frame on desktop and a horizontal strip
      // on a phone (and in the lightbox); centre the thumbnail on whichever axis
      // actually scrolls.
      const vertical = rail.scrollHeight > rail.clientHeight + 1;

      if (vertical) {
        const margin = thumbBox.height + 8;
        const fullyVisible =
          thumbBox.top >= railBox.top + margin && thumbBox.bottom <= railBox.bottom - margin;
        if (fullyVisible || (index === 0 && rail.scrollTop <= 1)) return;
        const center = thumbBox.top - railBox.top + rail.scrollTop + thumbBox.height / 2;
        rail.scrollTo({ top: center - rail.clientHeight / 2, behavior: 'smooth' });
      } else {
        const margin = thumbBox.width + 8;
        const fullyVisible =
          thumbBox.left >= railBox.left + margin && thumbBox.right <= railBox.right - margin;
        if (fullyVisible || (index === 0 && Math.abs(rail.scrollLeft) <= 1)) return;
        const center = thumbBox.left - railBox.left + rail.scrollLeft + thumbBox.width / 2;
        rail.scrollTo({ left: center - rail.clientWidth / 2, behavior: 'smooth' });
      }
    }

    #step(direction) {
      this.#goTo(this.activeIndex + direction);
    }

    showMedia(slide) {
      const index = this.slides.indexOf(slide);
      if (index >= 0) this.#goTo(index);
    }

    #slideLeft(slide) {
      return slide.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left
        + this.viewport.scrollLeft - this.viewport.clientLeft;
    }

    #settle() {
      clearTimeout(this.settleTimer);
      clearTimeout(this.snapTimer);
      this.viewport.style.scrollSnapType = '';
      const index = this.slides.reduce((best, slide, i) =>
        Math.abs(this.#slideLeft(slide) - this.viewport.scrollLeft)
          < Math.abs(this.#slideLeft(this.slides[best]) - this.viewport.scrollLeft) ? i : best, 0);
      this.#setActive(index);
    }

    #setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.counters.forEach((counter) => { counter.textContent = index + 1; });
      this.style.setProperty('--gallery-index', index);
      const button360 = this.querySelector('[data-gallery-360]');
      if (button360) button360.hidden = index !== 0;
      this.#markThumb(index);
      this.slides.forEach((slide, i) => {
        if (i !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    #goTo(index, behavior = 'smooth') {
      const next = Math.min(this.slides.length - 1, Math.max(0, index));
      const slide = this.slides[next];
      if (!slide || !this.viewport) return;
      this.#setActive(next);
      // Mandatory scroll-snap cancels a programmatic smooth scroll — the snap yanks it
      // straight back to the current slide. Lift the snap for the length of the move,
      // then restore it so a swipe still snaps. (Scroll only the track, never ancestors.)
      const viewport = this.viewport;
      viewport.style.scrollSnapType = 'none';
      viewport.scrollTo({ left: this.#slideLeft(slide), behavior });
      clearTimeout(this.snapTimer);
      this.snapTimer = setTimeout(() => {
        this.#settle();
      }, 1200);
    }

    /* Driven by the observer rather than by the click, so the highlight also follows
       a swipe or a variant change. */
    #markThumb(index) {
      this.thumbs?.forEach((thumb, i) => {
        const selected = i === index;
        thumb.setAttribute('aria-selected', String(selected));
        if (selected) this.#revealThumb(thumb);
      });
    }
  }

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGallery);
  }

  /**
   * Add to cart without leaving the page.
   *
   * The form still posts to /cart/add on its own if this fails, so the fallback is
   * the browser's default behaviour rather than a broken button.
   */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || typeof window.addToCart !== 'function') return;

    event.preventDefault();
    const button = form.querySelector('[data-add-button]');

    if (button) {
      button.disabled = true;
      // aria-busy drives the spinner and also announces the wait to screen readers;
      // the label is left in place so the button keeps its width.
      button.setAttribute('aria-busy', 'true');
    }

    try {
      const id = form.querySelector('[data-variant-id]')?.value;
      const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
      // Gift set: fold any chosen gifts into the same request so the scooter and its
      // gifts go up together — or not at all.
      const giftSet = document.querySelector('gift-set');
      const gifts =
        giftSet && typeof giftSet.getSelectedItems === 'function'
          ? giftSet.getSelectedItems(id)
          : [];
      await window.addToCart([{ id: Number(id), quantity }, ...gifts]);
    } catch (error) {
      console.error('[product]', error);
      form.submit();
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  });

  // Quantity steppers on the product form. Delegated, so the buttons keep working
  // if the form is re-rendered.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-step]');
    if (!button) return;

    const input = button.parentElement?.querySelector('.quantity__input');
    if (!input) return;

    const next = Number(input.value || 1) + Number(button.dataset.quantityStep);
    input.value = Math.max(Number(input.min || 1), next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /**
   * The inline "read more" is pinned to the end of the clamped description. It should
   * only appear when the text actually overflows — otherwise it would cover a short
   * description with a control that opens the very same text.
   */
  const syncDescriptionMore = () => {
    document.querySelectorAll('.product-description').forEach((desc) => {
      const body = desc.querySelector('.product-description__body--clamped');
      const more = desc.querySelector('.product-description__more');
      if (!body || !more) return;
      more.hidden = body.scrollHeight <= body.clientHeight + 1;
    });
  };

  requestAnimationFrame(syncDescriptionMore);
  window.addEventListener('resize', syncDescriptionMore);
  // Line heights settle once the web fonts land, which can change whether it overflows.
  document.fonts?.ready?.then(syncDescriptionMore);
})();
